/**
 * Screen-level urgency effects for the final 5 minutes:
 *  - red edge vignette/bloom that intensifies toward zero
 *  - final-minute banner
 *  - big centered seconds (≤10s) with a per-second pulse
 *  - optional WebAudio tick/heartbeat (OFF by default; choice saved in localStorage)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FINAL_MINUTE, FINAL_TEN, URGENCY_WINDOW, type UrgencyState } from './urgency';

const SOUND_KEY = 'nt3d_countdown_sound';

export function useCountdownSound() {
  const [on, setOn] = useState<boolean>(() => {
    try { return localStorage.getItem(SOUND_KEY) === 'on'; } catch { return false; }
  });
  const ctxRef = useRef<AudioContext | null>(null);
  const ensure = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === 'suspended') void ctxRef.current.resume();
    return ctxRef.current;
  }, []);
  const toggle = useCallback(() => {
    setOn(prev => {
      const next = !prev;
      try { localStorage.setItem(SOUND_KEY, next ? 'on' : 'off'); } catch { /* ignore */ }
      if (next) ensure();
      return next;
    });
  }, [ensure]);
  /** one heartbeat "lub-dub"; pitch/level rise with urgency */
  const beat = useCallback((level: number) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const thump = (at: number, freq: number, gain: number) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, at);
      o.frequency.exponentialRampToValueAtTime(freq * 0.5, at + 0.12);
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(gain, at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
      o.connect(g).connect(ctx.destination);
      o.start(at);
      o.stop(at + 0.2);
    };
    const base = 70 + level * 60;
    const vol = 0.08 + level * 0.12;
    thump(now, base, vol);
    thump(now + 0.14, base * 1.25, vol * 0.7);
    if (level > 0.8) thump(now + 0.3, 1200, 0.03); // tick on top in the final minute
  }, []);
  // Resume on first user gesture if the saved choice is ON (autoplay policy).
  useEffect(() => {
    if (!on) return;
    const h = () => ensure();
    window.addEventListener('pointerdown', h, { once: true });
    window.addEventListener('keydown', h, { once: true });
    return () => { window.removeEventListener('pointerdown', h); window.removeEventListener('keydown', h); };
  }, [on, ensure]);
  return { soundOn: on, toggleSound: toggle, beat };
}

export function SoundToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="holo-iconbtn" onClick={onToggle} aria-pressed={on}
      title={on ? 'Countdown sound: on' : 'Countdown sound: off'} aria-label={on ? 'Mute countdown sound' : 'Enable countdown sound'}>
      {on ? (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 9h4l5-4v14l-5-4H4z" /><path d="M16 9a4 4 0 010 6M18.5 6.5a8 8 0 010 11" /></svg>
      ) : (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 9h4l5-4v14l-5-4H4z" /><path d="M17 9l5 6M22 9l-5 6" /></svg>
      )}
    </button>
  );
}

export default function UrgencyOverlay({ timeRemaining, urgency, soundOn, beat }: {
  timeRemaining: number; urgency: UrgencyState; soundOn: boolean; beat: (level: number) => void;
}) {
  const t = timeRemaining;
  const live = t > 0 && t < URGENCY_WINDOW;
  // Sound: beat cadence follows the heartbeat (every 2s at 5:00 → every second in the last minute)
  const lastBeat = useRef(0);
  useEffect(() => {
    if (!soundOn || !live) return;
    const every = t < FINAL_MINUTE ? 1 : t < 150 ? 1.5 : 2;
    const now = performance.now();
    if (now - lastBeat.current >= every * 1000 - 120) { lastBeat.current = now; beat(urgency.level); }
  }, [t, soundOn, live, beat, urgency.level]);

  if (!urgency.active || t < 0) return null;
  return (
    <>
      <div className="holo-vignette" aria-hidden="true" style={{ opacity: 0.25 + urgency.level * 0.75 }} />
      {t > 0 && t < FINAL_MINUTE && (
        <div className="holo-urgent-banner" role="alert">
          <span className="dot" /> FINAL MINUTE — ANY KEY RESETS THE CLOCK +30s
        </div>
      )}
      {t > 0 && t <= FINAL_TEN && (
        <div className="holo-bigsec" aria-hidden="true">
          <span key={t}>{t}</span>
        </div>
      )}
    </>
  );
}
